import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { Info as InfoIcon, FileText, Film, SquarePen, Trash2, Plane } from "lucide-react";
import { apiJson } from "../lib/queryClient";
import {
  ALL_PRESETS,
  searchPresets,
  getDefaultCategoryForPreset,
  type DronePreset,
} from "@client/lib/dronePresets";
import {
  getCinematicSettingsForModel,
  DRONE_MOVES_GUIDE,
  type DroneCinematicSettings,
} from "@client/lib/droneCinematicSettings";

type Drone = {
  id: string;
  name: string;
  manufacturer: string | null;
  model: string | null;
  classMark: string;
  mtomGrams: number;
  openCategory: string;
  droneType: string;
};

type FormState = {
  id?: string;
  name: string;
  manufacturer: string;
  model: string;
  classMark: string;
  mtomGrams: string;
  openCategory: string;
  droneType: string;
};

const EMPTY_FORM: FormState = {
  name: "",
  manufacturer: "",
  model: "",
  classMark: "C0",
  mtomGrams: "249",
  openCategory: "A1",
  droneType: "standard",
};

const DEFAULT_LIMITS = {
  maxWind: 10,
  maxGusts: 12,
  maxRain: 0.5,
  minTemp: 0,
  maxTemp: 40,
  minCloudCeiling: 50,
  minVisibility: 5000,
};

const DEFAULT_COMPLIANCE = {
  operatorRegistered: false,
  eIdLabeledOnDrone: false,
  insuranceActive: false,
  competencyA1A3: false,
  certificateA2: false,
  remoteIdConfigured: false,
};

export default function Drones() {
  const qc = useQueryClient();
  const { data: drones = [], isLoading } = useQuery<Drone[]>({ queryKey: ["/api/drones"] });

  const [editing, setEditing] = useState<FormState | null>(null);
  const [cinematicFor, setCinematicFor] = useState<{ model: string | null; name: string } | null>(null);
  const location = useLocation();
  const editDroneId = (location.state as { editDroneId?: string } | null)?.editDroneId;

  useEffect(() => {
    if (!editDroneId || editing) return;
    const d = drones.find((x) => x.id === editDroneId);
    if (!d) return;
    setEditing({
      id: d.id,
      name: d.name,
      manufacturer: d.manufacturer || "",
      model: d.model || "",
      classMark: d.classMark,
      mtomGrams: String(d.mtomGrams),
      openCategory: d.openCategory,
      droneType: d.droneType,
    });
  }, [editDroneId, drones, editing]);

  const saveMutation = useMutation({
    mutationFn: async (form: FormState) => {
      const payload = {
        name: form.name.trim(),
        manufacturer: form.manufacturer.trim() || null,
        model: form.model.trim() || null,
        classMark: form.classMark,
        mtomGrams: parseInt(form.mtomGrams, 10) || 0,
        openCategory: form.openCategory,
        droneType: form.droneType,
        hasCameraOrSensor: true,
        compliance: DEFAULT_COMPLIANCE,
        limits: DEFAULT_LIMITS,
      };
      if (form.id) {
        return apiJson("PUT", `/api/drones/${form.id}`, payload);
      }
      return apiJson("POST", "/api/drones", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/drones"] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiJson("DELETE", `/api/drones/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/drones"] }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    saveMutation.mutate(editing);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          className="btn-primary"
          onClick={() => setEditing({ ...EMPTY_FORM })}
        >
          + Neue Drohne
        </button>
      </div>

      {isLoading ? (
        <div className="text-center text-[var(--color-app-text-secondary)] py-10">Lädt …</div>
      ) : drones.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-[var(--color-app-text-secondary)]">Noch keine Drohnen angelegt.</p>
          <button
            className="btn-primary mt-4"
            onClick={() => setEditing({ ...EMPTY_FORM })}
          >
            Erste Drohne anlegen
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {drones.map((d) => (
            <div key={d.id} className="card" data-testid={`card-drone-${d.id}`}>
              <div className="flex items-start gap-3">
                <div className="size-11 rounded-xl bg-brand-50 grid place-items-center shrink-0">
                  <Plane size={22} className="text-brand-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-[var(--color-app-text)] truncate">{d.name}</div>
                    <span className="text-[11px] px-2 py-0.5 bg-brand-100 text-brand-700 rounded-md font-semibold shrink-0">
                      {d.classMark}
                    </span>
                  </div>
                  <div className="text-xs text-[var(--color-app-text-secondary)] mt-0.5">
                    {d.mtomGrams} g · {d.openCategory} · {typeLabel(d.droneType)}
                  </div>
                  {[d.manufacturer, d.model].filter(Boolean).length > 0 ? (
                    <div className="text-xs text-[var(--color-app-text-tertiary)] mt-0.5 truncate">
                      {[d.manufacturer, d.model].filter(Boolean).join(" ")}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[var(--color-app-border)] flex items-center gap-1">
                <Link
                  to={`/drohnen/${d.id}/info`}
                  className="grid place-items-center size-9 rounded-lg text-[var(--color-app-text-secondary)] hover:bg-[var(--color-app-bg-tertiary)] transition-colors"
                  data-testid={`link-drone-info-${d.id}`}
                  title="Info"
                  aria-label="Info"
                >
                  <InfoIcon size={18} />
                </Link>
                <Link
                  to={`/drohnen/${d.id}/historie`}
                  className="grid place-items-center size-9 rounded-lg text-[var(--color-app-text-secondary)] hover:bg-[var(--color-app-bg-tertiary)] transition-colors"
                  title="Historie"
                  aria-label="Historie"
                >
                  <FileText size={18} />
                </Link>
                <button
                  className="grid place-items-center size-9 rounded-lg text-[var(--color-app-text-secondary)] hover:bg-[var(--color-app-bg-tertiary)] transition-colors"
                  onClick={() => setCinematicFor({ model: d.model, name: d.name })}
                  title="Aufnahme"
                  aria-label="Aufnahme"
                >
                  <Film size={18} />
                </button>
                <button
                  className="grid place-items-center size-9 rounded-lg text-[var(--color-app-text-secondary)] hover:bg-[var(--color-app-bg-tertiary)] transition-colors"
                  onClick={() =>
                    setEditing({
                      id: d.id,
                      name: d.name,
                      manufacturer: d.manufacturer || "",
                      model: d.model || "",
                      classMark: d.classMark,
                      mtomGrams: String(d.mtomGrams),
                      openCategory: d.openCategory,
                      droneType: d.droneType,
                    })
                  }
                  title="Bearbeiten"
                  aria-label="Bearbeiten"
                  data-testid={`button-edit-drone-${d.id}`}
                >
                  <SquarePen size={18} />
                </button>
                <div className="flex-1" />
                <button
                  className="grid place-items-center size-9 rounded-lg text-[var(--color-nogo)] hover:bg-[var(--color-nogo-bg)] transition-colors"
                  onClick={() => {
                    if (confirm(`"${d.name}" wirklich löschen?`)) deleteMutation.mutate(d.id);
                  }}
                  title="Löschen"
                  aria-label="Löschen"
                  data-testid={`button-delete-drone-${d.id}`}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-lg font-semibold">
              {editing.id ? "Drohne bearbeiten" : "Neue Drohne"}
            </h2>

            {!editing.id && (
              <PresetPicker
                onPick={(p) =>
                  setEditing({
                    ...editing,
                    name: editing.name.trim() || `${p.manufacturer} ${p.model}`,
                    manufacturer: p.manufacturer,
                    model: p.model,
                    classMark: p.classMark,
                    mtomGrams: String(p.mtomGrams),
                    openCategory: getDefaultCategoryForPreset(p),
                    droneType: p.droneType,
                  })
                }
              />
            )}

            <div>
              <label className="label">Name</label>
              <input
                className="input"
                required
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="z.B. Mavic 3 Pro"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Hersteller</label>
                <input
                  className="input"
                  value={editing.manufacturer}
                  onChange={(e) => setEditing({ ...editing, manufacturer: e.target.value })}
                  placeholder="DJI"
                />
              </div>
              <div>
                <label className="label">Modell</label>
                <input
                  className="input"
                  value={editing.model}
                  onChange={(e) => setEditing({ ...editing, model: e.target.value })}
                  placeholder="Mavic 3 Pro"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">Klasse</label>
                <select
                  className="input"
                  value={editing.classMark}
                  onChange={(e) => setEditing({ ...editing, classMark: e.target.value })}
                >
                  {["C0", "C1", "C2", "C3", "C4", "C5", "C6", "legacy"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">MTOM (g)</label>
                <input
                  type="number"
                  className="input"
                  required
                  min={0}
                  value={editing.mtomGrams}
                  onChange={(e) => setEditing({ ...editing, mtomGrams: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Kategorie</label>
                <select
                  className="input"
                  value={editing.openCategory}
                  onChange={(e) => setEditing({ ...editing, openCategory: e.target.value })}
                >
                  {["A1", "A2", "A3"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="label">Typ</label>
              <select
                className="input"
                value={editing.droneType}
                onChange={(e) => setEditing({ ...editing, droneType: e.target.value })}
              >
                <option value="selfie">Selfie / Mini</option>
                <option value="standard">Standard</option>
                <option value="fpv_only">FPV only</option>
                <option value="hybrid_fpv">Hybrid FPV</option>
              </select>
            </div>

            {saveMutation.error && (
              <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] rounded-md px-3 py-2">
                {(saveMutation.error as Error).message}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                className="btn-secondary flex-1"
                onClick={() => setEditing(null)}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? "Speichert …" : "Speichern"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {cinematicFor && (
        <CinematicModal
          model={cinematicFor.model}
          name={cinematicFor.name}
          onClose={() => setCinematicFor(null)}
        />
      )}
    </div>
  );
}

function typeLabel(t: string) {
  switch (t) {
    case "selfie": return "Selfie";
    case "fpv_only": return "FPV";
    case "hybrid_fpv": return "Hybrid";
    default: return "Standard";
  }
}

function PresetPicker({ onPick }: { onPick: (p: DronePreset) => void }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const results = useMemo<DronePreset[]>(() => {
    const q = query.trim();
    const list = q ? searchPresets(q) : ALL_PRESETS;
    return list.slice(0, 30);
  }, [query]);

  return (
    <div className="bg-brand-50 border border-brand-100 rounded-md p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-brand-700 uppercase tracking-wide">
          DJI Drohnen-Datenbank
        </div>
        <button
          type="button"
          className="text-xs text-brand-600 hover:underline"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Verbergen" : "Drohne auswählen"}
        </button>
      </div>
      {open && (
        <>
          <input
            className="input mb-2"
            placeholder="z.B. Mini 4 Pro, Mavic 3, Air 3 …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="max-h-56 overflow-y-auto rounded-md bg-white border border-[var(--color-app-border)] divide-y divide-[var(--color-app-border)]">
            {results.length === 0 ? (
              <div className="px-3 py-3 text-sm text-[var(--color-app-text-secondary)]">Keine Treffer.</div>
            ) : (
              results.map((p) => (
                <button
                  key={`${p.manufacturer}-${p.model}`}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-[var(--color-app-bg-secondary)] flex items-center justify-between gap-3"
                  onClick={() => {
                    onPick(p);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <div>
                    <div className="text-sm font-medium text-[var(--color-app-text)]">
                      {p.manufacturer} {p.model}
                    </div>
                    <div className="text-xs text-[var(--color-app-text-secondary)]">
                      {p.year} · {p.mtomGrams} g · max {p.maxWindMs} m/s · {p.notes || "—"}
                    </div>
                  </div>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-brand-100 text-brand-700 font-semibold shrink-0">
                    {p.classMark}
                  </span>
                </button>
              ))
            )}
          </div>
          <p className="text-[11px] text-[var(--color-app-text-secondary)] mt-2">
            Übernimmt Klasse, MTOM, Kategorie und Typ. Du kannst die Werte danach
            anpassen.
          </p>
        </>
      )}
    </div>
  );
}

function CinematicModal({
  model,
  name,
  onClose,
}: {
  model: string | null;
  name: string;
  onClose: () => void;
}) {
  const settings: DroneCinematicSettings | null = getCinematicSettingsForModel(model, name);
  const [tab, setTab] = useState<"settings" | "moves">(settings ? "settings" : "moves");

  return (
    <Modal onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {settings ? settings.displayName : "Cinematic-Guide"}
          </h2>
          <button
            type="button"
            className="text-[var(--color-app-text-tertiary)] hover:text-[var(--color-app-text-secondary)] text-xl leading-none"
            onClick={onClose}
            aria-label="Schließen"
          >
            &times;
          </button>
        </div>

        {settings ? (
          <div className="flex gap-2">
            <button
              type="button"
              className={tab === "settings" ? "btn-primary text-xs px-3 py-1.5 flex-1" : "btn-secondary text-xs px-3 py-1.5 flex-1"}
              onClick={() => setTab("settings")}
            >
              Kameraeinstellungen
            </button>
            <button
              type="button"
              className={tab === "moves" ? "btn-primary text-xs px-3 py-1.5 flex-1" : "btn-secondary text-xs px-3 py-1.5 flex-1"}
              onClick={() => setTab("moves")}
            >
              Flugmanöver
            </button>
          </div>
        ) : null}

        <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
          {tab === "settings" && settings ? (
            <>
              <p className="text-xs text-[var(--color-app-text-secondary)]">
                Empfohlene cinematische Kamera- und Tuning-Einstellungen für die{" "}
                {settings.displayName}.
              </p>
              {settings.groups.map((group) => (
                <div key={group.title} className="bg-[var(--color-app-bg-secondary)] rounded-md p-3">
                  <div className="text-xs font-semibold text-brand-700 uppercase tracking-wide mb-1.5">
                    {group.title}
                  </div>
                  <ul className="space-y-1">
                    {group.items.map((item, idx) => (
                      <li key={idx} className="text-sm text-[var(--color-app-text)] flex gap-2">
                        <span className="text-brand-500 mt-1.5 shrink-0">·</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <a
                href={settings.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-[var(--color-app-text-tertiary)] hover:underline inline-block"
              >
                Quelle: {settings.source}
              </a>
            </>
          ) : (
            <>
              <p className="text-xs text-[var(--color-app-text-secondary)]">
                {DRONE_MOVES_GUIDE.title} — funktioniert mit jeder Drohne.
              </p>
              {DRONE_MOVES_GUIDE.moves.map((move) => (
                <div key={move.title} className="bg-[var(--color-app-bg-secondary)] rounded-md p-3">
                  <div className="text-xs font-semibold text-brand-700 uppercase tracking-wide mb-1.5">
                    {move.title}
                  </div>
                  {move.sections.map((section, sIdx) => (
                    <div key={sIdx} className={sIdx > 0 ? "mt-2" : undefined}>
                      {section.label ? (
                        <div className="text-xs font-semibold text-[var(--color-app-text-secondary)]">{section.label}</div>
                      ) : null}
                      <ul className="space-y-1 mt-1">
                        {section.steps.map((step, stepIdx) => (
                          <li key={stepIdx} className="text-sm text-[var(--color-app-text)] flex gap-2">
                            <span className="text-brand-500 mt-1.5 shrink-0">·</span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
              <a
                href={DRONE_MOVES_GUIDE.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-[var(--color-app-text-tertiary)] hover:underline inline-block"
              >
                Quelle: {DRONE_MOVES_GUIDE.source}
              </a>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 grid place-items-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
